<template>
  <div class="flex flex-col h-screen">
    <PageHeader />
    <div class="flex flex-1 overflow-hidden">
      <!-- Sidebar for desktop/tablet -->
      <SidebarMenu
          v-if="!isMobile"
          @toggle="handleToggle"
          ref="sidebar"
          @quiz-country-name="updateQuizCountryName"
          @quiz-type-changed="updateQuizType"
          @update-active-tab="updateActiveTab"
          @quiz-complete="completeQuiz"
          @next-clue="nextClue"
          @map-settings-changed="handleMapSettingsChanged"
          @next-amazing-place="handleNextAmazingPlace"
          @navigate-to-place="handleNavigateToPlace"
      />
      <!-- Main Content -->
      <div :class="`flex-1 transition-all duration-300 ${sidebarOpen && !isMobile ? 'w-2/3' : 'w-full'}`">
        <!-- Mapbox Map (Default and Only Provider) -->
        <MapboxMap
            :activeTab="activeTab"
            :quizCountryName="quizCountryName"
            :quizType="quizType"
            :mapSettings="mapSettings"
            @update-info="updateInfo"
            @loading="setLoading"
            @quiz-complete="handleQuizComplete"
            @wrong-guess="handleWrongGuess"
            @next-clue="nextClue"
            @close-info="closeInfo"
            ref="mapComponent"
        />
      </div>
    </div>
    <!-- Sidebar for mobile -->
    <SidebarMenu
        v-if="isMobile"
        @toggle="handleToggle"
        ref="sidebar"
        @quiz-country-name="updateQuizCountryName"
        @quiz-type-changed="updateQuizType"
        @update-active-tab="updateActiveTab"
        @quiz-complete="completeQuiz"
        @next-clue="nextClue"
        @map-settings-changed="handleMapSettingsChanged"
        @next-amazing-place="handleNextAmazingPlace"
        @navigate-to-place="handleNavigateToPlace"
    />
  </div>
</template>

<script>
import PageHeader from './PageHeader.vue';
import SidebarMenu from './SidebarMenu.vue';
import MapboxMap from './MapboxMap.vue';

export default {
  components: {
    PageHeader,
    SidebarMenu,
    MapboxMap,
  },
  data() {
    return {
      sidebarOpen: true,
      activeTab: 'info',
      quizCountryName: '',
      quizType: null, // 'clues' or 'flag'
      isMobile: window.innerWidth <= 768, // Detect mobile screen size
      mapSettings: {
        style: 'satellite',
        countryOverlay: 'white',
        showPlaceNames: true,
        showTectonicPlates: false,
        show3DTerrain: true,
        show3DBuildings: false,
        show3DModels: false
      }
    };
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
    // Check if there's a slug in the route and handle it (with a small delay to ensure components are ready)
    this.$nextTick(() => {
      setTimeout(() => {
        this.handleRouteSlug();
        this.handleSearchQuery();
      }, 100);
    });
  },
  watch: {
    '$route'() {
      // Handle route changes (when user navigates to a different slug)
      this.handleRouteSlug();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  methods: {
    handleToggle(isOpen) {
      this.sidebarOpen = isOpen;
    },
    updateQuizCountryName(name) {
      this.quizCountryName = name;
    },
    updateQuizType(type) {
      console.log('MapApp.vue: updateQuizType called with:', type);
      this.quizType = type;
    },
    updateActiveTab(tab) {
      this.activeTab = tab;
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleMapSettingsChanged(newSettings) {
      this.mapSettings = { ...newSettings };
    },
    updateInfo(data) {
      this.$refs.sidebar.updateInfo(data);
    },
    setLoading(isLoading, context) {
      this.$refs.sidebar.setLoading(isLoading, context);
    },
    completeQuiz() {
      this.$refs.sidebar.completeQuiz();
    },
    handleQuizComplete({lat, lng}) {
      this.quizCoordinates = {lat, lng};
      this.$refs.sidebar.completeQuiz();
    },
    nextClue() {
      this.$refs.sidebar.nextClue();
    },
    handleWrongGuess() {
      console.log('MapApp.vue: handleWrongGuess called');
      this.$refs.sidebar.handleWrongGuess();
    },
    handleNextAmazingPlace() {
      this.$refs.mapComponent.goToNextAmazingPlace();
    },
    closeInfo() {
      this.$refs.sidebar.closeInfo();
    },
    handleNavigateToPlace(place) {
      // Navigate to the place coordinates on the map
      if (this.$refs.mapComponent && place.coordinates) {
        this.$refs.mapComponent.flyToCoordinates(
          place.coordinates.latitude, 
          place.coordinates.longitude,
          place.placeName
        );
      }
    },
    async handleRouteSlug() {
      // Check if we have a slug parameter in the route
      const slug = this.$route.params.slug;
      const routeName = this.$route.name;
      
      if (slug) {
        console.log(`Route contains slug: ${slug}, route name: ${routeName}`);
        
        if (routeName === 'Article') {
          await this.handleArticleRoute(slug);
        } else if (routeName === 'Location') {
          await this.handleLocationRoute(slug);
        }
      }
    },
    async handleLocationRoute(slug) {
      try {
        // Show loading state
        this.setLoading(true, 'Loading shared location...');
        
        // Fetch place data by slug
        const placeData = await this.fetchPlaceBySlug(slug);
        
        if (placeData && placeData.lat && placeData.lng) {
          // Navigate to the place on the map (skip fetch since we already have the data)
          this.$refs.mapComponent?.flyToCoordinates(
            placeData.lat, 
            placeData.lng, 
            placeData.name,
            true // skipFetch = true, we already have all the data
          );
          
          // Update the info panel with the place data
          this.updateInfo(placeData);
          
          // Make sure info tab is active
          this.updateActiveTab('info');
        }
      } catch (error) {
        console.error('Error loading place by slug:', error);
        
        // Show error message to user
        if (error.message === 'Place not found') {
          this.showNotFoundMessage(slug);
        } else {
          this.showErrorMessage('Failed to load the shared location. Please try again.');
        }
      } finally {
        this.setLoading(false);
      }
    },
    async handleArticleRoute(slug) {
      try {
        // Show loading state
        this.setLoading(true, 'Loading article...');
        
        // Fetch article data by slug
        const articleData = await this.fetchArticleBySlug(slug);
        
        if (articleData && articleData.placeSlug) {
          // First, navigate to the place on the map
          const placeData = await this.fetchPlaceBySlug(articleData.placeSlug);
          
          if (placeData && placeData.lat && placeData.lng) {
            // Navigate to the place on the map
            this.$refs.mapComponent?.flyToCoordinates(
              placeData.lat, 
              placeData.lng, 
              placeData.name,
              true // skipFetch = true, we already have all the data
            );
            
            // Update the info panel with the place data
            this.updateInfo(placeData);
            
            // Make sure info tab is active
            this.updateActiveTab('info');
            
            // After a short delay, open the article modal
            setTimeout(() => {
              this.$store.dispatch('moreInfo/openModal', {
                placeName: placeData.name,
                country: placeData.country,
                itemSlug: articleData.itemSlug,
                slug: articleData.slug,
                placeSlug: articleData.placeSlug
              });
            }, 1000); // 1 second delay to allow map to settle
          }
        } else {
          this.showErrorMessage('Article not found or missing location data.');
        }
      } catch (error) {
        console.error('Error loading article by slug:', error);
        this.showErrorMessage('Failed to load the article. Please try again.');
      } finally {
        this.setLoading(false);
      }
    },
    async fetchPlaceBySlug(slug) {
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/placeInfo/slug/${slug}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Place not found');
          }
          throw new Error('Failed to fetch place data');
        }
        
        const placeData = await response.json();
        return placeData;
      } catch (error) {
        console.error('Error fetching place by slug:', error);
        throw error;
      }
    },
    async fetchArticleBySlug(slug) {
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/moreInfo?slug=${encodeURIComponent(slug)}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Article not found');
          }
          throw new Error('Failed to fetch article data');
        }
        
        const articleData = await response.json();
        return articleData;
      } catch (error) {
        console.error('Error fetching article by slug:', error);
        throw error;
      }
    },
    showNotFoundMessage(slug) {
      // Update the info panel with a not found message
      const notFoundData = {
        name: 'Location Not Found',
        description: `Sorry, we couldn't find the location "${slug}". This shared link may be invalid or the location may have been removed.`,
        slug: slug,
        country: 'Unknown',
        country_code: 'unknown',
        landmarks: [],
        famous_places: [],
        traditional_foods: [],
        native_species: [],
        famous_people: [],
        important_dates: [],
        languages_spoken: [],
        history: 'This location could not be found in our database.',
        coordinates: '0,0',
        territorial_divisions: {
          region: 'Unknown'
        },
        images: []
      };
      
      this.updateInfo(notFoundData);
      this.updateActiveTab('info');
    },
    showErrorMessage(message) {
      // Update the info panel with a generic error message
      const errorData = {
        name: 'Error Loading Location',
        description: message,
        slug: 'error',
        country: 'Unknown',
        country_code: 'unknown',
        landmarks: [],
        famous_places: [],
        traditional_foods: [],
        native_species: [],
        famous_people: [],
        important_dates: [],
        languages_spoken: [],
        history: 'An error occurred while trying to load this location.',
        coordinates: '0,0',
        territorial_divisions: {
          region: 'Unknown'
        },
        images: []
      };
      
      this.updateInfo(errorData);
      this.updateActiveTab('info');
    },
    handleSearchQuery() {
      // Check if there's a search parameter in the route
      const searchQuery = this.$route.query.search;
      
      if (searchQuery) {
        console.log(`Route contains search query: ${searchQuery}`);
        // Trigger search in the map component
        this.$nextTick(() => {
          if (this.$refs.mapComponent && this.$refs.mapComponent.performSearch) {
            this.$refs.mapComponent.performSearch(searchQuery);
          }
        });
      }
    },
  },
};
</script>

<style>
/* Ensure body and html take up full height */
html, body, #app {
  height: 100%;
  margin: 0;
}
</style>
